/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Box from "components/generals/Box";
import { FC, useEffect, useRef, useState } from "react";
import { AppointmentInterface, HeaderEnum, ProgressNoteInterface } from "medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { getFromISOYYYYMMDDDateFormat } from "utils/date.utils";
import { Caption, H3, H5 } from "components/generals/_Titles";
import ButtonDialog from "components/generals/ButtonDialog";
import HeaderPersonInformation from "./HeaderPersonInformation";
import { useTranslation } from "react-i18next";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";

/**
 * Properties for the HistoryProgressNoteCard component.
 * 
 * @interface HistoryProgressNoteCardProps
 * @property {string} [appointmentId] - Appointment ID to load progress note for
 * @property {string[]} [sectionsToPrint] - Array of section IDs to print
 * @property {boolean} [printbtn] - Whether to show print button
 */
interface HistoryProgressNoteCardProps {
  appointmentId?: string;
  sectionsToPrint?: string[];
  printbtn?: boolean;
}

/**
 * History progress note card component for displaying clinical documentation.
 * Shows complete progress note with patient demographics and clinical sections.
 * 
 * Features:
 * - Loads appointment and progress note data by ID
 * - Displays patient header with demographics
 * - Shows all progress note sections
 * - Print functionality with selective section printing
 * - Permission-based delete button
 * - Auto-print on sectionsToPrint change
 * - Formatted date display
 * - HTML content rendering
 * - Reference to printable content
 * 
 * Clinical Sections (typical):
 * - Chief Complaint
 * - History of Present Illness (HPI)
 * - Review of Systems (ROS)
 * - Physical Examination
 * - Assessment/Diagnosis
 * - Plan/Treatment
 * - Medications
 * - Lab Orders
 * - Imaging Orders
 * 
 * Print Behavior:
 * - Opens new window for printing
 * - Includes CSS styles from main document
 * - Selective section printing via sectionsToPrint array
 * - Auto-triggers print dialog
 * 
 * TODO:
 * - Check alternatives for getTextFromHTML function
 * 
 * @param {HistoryProgressNoteCardProps} props - Component properties
 * @returns {JSX.Element} Progress note history card with print capability
 */
const HistoryProgressNoteCard: FC<HistoryProgressNoteCardProps> = ({
  appointmentId,
  sectionsToPrint = [],
  printbtn,
}) => {
  const { t } = useTranslation();
  const [appointmentModel, setAppointmentModel] = useState<Partial<AppointmentInterface>>();
  const [progressNoteModel, setProgressNoteModel] = useState<Partial<ProgressNoteInterface>>();
  const [_isLoading, _error, sendRequest] = useHttp();
  const printableContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (appointmentId) {
      sendRequest(
        CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
        onGetAppointment,
        "GET",
        undefined,
        [
          {
            header: HeaderEnum.RESOURCE_ID,
            value: appointmentId,
          },
        ]
      );
    }
  }, [appointmentId]);

  useEffect(() => {
    if (sectionsToPrint.length > 0) {
      handlePrint(sectionsToPrint);
    }
  }, [sectionsToPrint]);

  const onGetAppointment = (appointment: AppointmentInterface) => {
    console.log(appointment);
    if (appointment) {
      setAppointmentModel(appointment);
    } else {
      setAppointmentModel(null);
    }
    if (appointment.progressNote) {
      setProgressNoteModel(appointment.progressNote);
    } else setProgressNoteModel(null);
  };
  function getTextFromHtml(html) {
    return html
      .replace(/<\/?p>/g, "\n")
      .replace(/<\/?strong>/g, "")
      .replace(/<\/?em>/g, "")
      .replace(/<\/?u>/g, "")
      .replace(/<\/?code>/g, "")
      .replace(/<\/?ul>/g, "")
      .replace(/<\/?ol[^>]*>/g, "")
      .replace(/<\/?li>/g, "\n- ")
      .replace(/<br\/?>/g, "\n")
      .replace(/<[^>]*>/g, "");
  }

  const accordionsList = [
    { id: "chief-complaints", name: t("progressNoteForm.chiefComplaints") },
    { id: "hpi", name: t("progressNoteForm.hpi") },
    { id: "vitals", name: t("progressNoteForm.vitals") },
    { id: "comments", name: t("progressNoteForm.comments") },
    /*TODO - All ids from others sections
    { id: "assessment-diagnosis", name: "Assessment / Diagnosis" },
    { id: "allergies-intolerance", name: "Allergies / Intolerance" },
    { id: "vaccine", name: "Vaccine" },
    { id: "patient-history", name: "Patient History" },
    { id: "current-visit", name: "Current Visit" },
    { id: "treatment", name: "Treatment" },
    { id: "medication", name: "Medication" },
    { id: "pharmacy", name: "Pharmacy" },
    { id: "care-plan", name: "Care Plan" },
    { id: "health-risk", name: "Health Risk" },
    { id: "preventine-medicine", name: "Preventive Medicine" },
    { id: "aditional-notes", name: "Additional Notes" },*/
  ];

  const handlePrint = (sectionsToPrint: string[]) => {
    const printableContent = printableContentRef.current;

    if (printableContent) {
      const tempDiv = document.createElement("div");

      const appendSection = (elementId: string) => {
        const sectionElement = document.getElementById(elementId);
        if (sectionElement) {
          tempDiv.appendChild(sectionElement.cloneNode(true));
        }
      };

      appendSection("personData");
      appendSection("reason");

      sectionsToPrint.forEach((sectionId) => {
        appendSection(sectionId);
      });

      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const printWindowWidth = 800;
      const printWindowHeight = 600;

      const left = screenWidth / 2 - printWindowWidth / 2;
      const top = screenHeight / 2 - printWindowHeight / 2;

      const printWindow = window.open(
        "",
        "",
        `width=${printWindowWidth},height=${printWindowHeight},left=${left},top=${top}`
      );
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title> ${t("progressNoteForm.progressNote")}</title>
              <style>
                body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0; 
                  border: 3px double #7b809a;
                  border-radius: 0.5rem;
                }
                .print-container{
                  margin: 10px;
                }
                h5 {                  
                  font-weight: bold;
                  margin-bottom: 10px;
                }
                .vitals-table {
                  display: flex;
                  flex-wrap: wrap;
                  align-items: flex-end;
                }
                .vitals-table > div {
                  width: 33.3%;
                  margin-bottom: 5px;
                }                
              </style>
            </head>
            <body>
              <div class="print-container">
                <H3>${t("progressNoteForm.progressNoteSummary")}</H3>
                ${tempDiv.innerHTML}
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }
  };

  return (
    <Card
      id="progressNoteCard"
      sx={{
        position: "sticky",
        top: 0,
      }}
      ref={printableContentRef}
    >
      {printbtn && (
        <Box mr={2} mt={2} textAlign={"right"}>
          <PermissionWrapper
            requiredPermissions={[PermissionsUser[EntityPermissions.PROGRESS_NOTE].PRINT]}
          >
            <ButtonDialog
              color={true}
              startIcon={true}
              disabled={false}
              onAcceptHandle={(e) => {
                handlePrint(e);
              }}
              buttonText={t("progressNoteForm.btnDialogPrint")}
              title={t("progressNoteForm.btnDialogTitle")}
              description={accordionsList}
              labelledbyID={t("progressNoteForm.btnDialogPrint")}
              describedbyID={"PrintDescription"}
            />
          </PermissionWrapper>
        </Box>
      )}
      <Box
        borderRadius="lg"
        ml={"1rem"}
        mt={2}
        width="calc(100% - 2rem)"
        height="calc(100% - 2rem)"
        paddingBottom={"1rem"}
      >
        <H3>{t("progressNoteForm.progressNoteSummary")}</H3>
        {appointmentModel ? (
          <>
            {progressNoteModel ? (
              <>
                <Grid id="personData" xs={12}>
                  <HeaderPersonInformation
                    personInformation={appointmentModel.patient.person}
                    progressNote={true}
                  />
                </Grid>
                <Grid id="reason" container spacing={1} mt={2}>
                  <Grid item xs={12}>
                    <H5>{t("progressNoteForm.reason")}:</H5>
                  </Grid>
                  <Grid item xs={12}>
                    <Caption>{appointmentModel.reason}</Caption>
                  </Grid>
                </Grid>
                <Grid id="chief-complaints" container spacing={1}>
                  <Grid item xs={12}>
                    <H5>{t("progressNoteForm.chiefComplaints")}:</H5>
                  </Grid>
                  <Grid item xs={12}>
                    <Caption>{progressNoteModel.chiefComplaints}</Caption>
                  </Grid>
                </Grid>
                <Grid id="hpi" container spacing={1} paddingTop={2}>
                  <Grid item xs={12}>
                    <H5>{t("progressNoteForm.hpi")}:</H5>
                  </Grid>
                  <Grid item xs={12}>
                    <Caption>{getTextFromHtml(progressNoteModel.hpi)}</Caption>
                  </Grid>
                </Grid>
                <Grid id="vitals" className="vitals-table" container spacing={1} paddingTop={2}>
                  <Grid item xs={12}>
                    <H5>{t("progressNoteForm.vitals")}:</H5>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.date")}:{" "}
                      {getFromISOYYYYMMDDDateFormat(
                        new Date(appointmentModel.vitals.registrationDate)
                      )}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.bp")}: {appointmentModel.vitals.bp}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.hr")}: {appointmentModel.vitals.hr}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.rr")}: {appointmentModel.vitals.rr}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.temp")}: {appointmentModel.vitals.temp}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.oxy")}: {appointmentModel.vitals.oxy}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.wt")}: {appointmentModel.vitals.wt}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.wtPercentage")}: {appointmentModel.vitals.wtPercentage}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.ht")}: {appointmentModel.vitals.ht}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.htPercentage")}: {appointmentModel.vitals.htPercentage}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.bmi")}: {appointmentModel.vitals.bmi}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.bmiPercentage")}: {appointmentModel.vitals.bmiPercentage}
                    </Caption>
                  </Grid>
                  <Grid item xs={6} md={3} lg={3}>
                    <Caption>
                      {t("progressNoteForm.painScale")}: {appointmentModel.vitals.painScale}
                    </Caption>
                  </Grid>
                  <Grid item xs={12}>
                    <Caption>
                      {t("progressNoteForm.note")}: {appointmentModel.vitals.note}
                    </Caption>
                  </Grid>
                </Grid>
                <Grid id="comments" container spacing={1} paddingTop={2}>
                  <Grid item xs={12}>
                    <H5>{t("progressNoteForm.comments")}:</H5>
                  </Grid>
                  <Grid item xs={12}>
                    <Caption>{progressNoteModel.comments}</Caption>
                  </Grid>
                </Grid>
              </>
            ) : (
              <H5>{t("progressNoteForm.noProgressInformation")}</H5>
            )}
          </>
        ) : (
          <H5>{t("progressNoteForm.noProgressInformation")}</H5>
        )}
      </Box>
    </Card>
  );
};

export default HistoryProgressNoteCard;
